import { supabase } from "@/integrations/supabase/client";
import { locationService } from "./locationService";

export interface FilterState {
  ageMin: number;
  ageMax: number;
  maxDistance: number;
  onlineOnly: boolean;
  withPhotoOnly: boolean;
  lookingFor: string[];
}

interface HometownLocation {
  lat: number;
  lng: number;
  city?: string;
  country?: string;
}

export type SortingType = "nearby" | "activity" | "new" | "most_buzzz" | "travelers";

// Helper function to get photos from either column
const getPhotoUrls = (profile: any): string[] => {
  // Prioritize photo_urls, fall back to photos
  if (profile.photo_urls && profile.photo_urls.length > 0) {
    return profile.photo_urls;
  }
  if (profile.photos && profile.photos.length > 0) {
    return profile.photos;
  }
  return [];
};

// Helper function to safely cast hometown data
const parseHometownLocation = (hometown: any): HometownLocation | null => {
  if (!hometown || typeof hometown !== 'object') {
    return null;
  }
  
  if (typeof hometown.lat === 'number' && typeof hometown.lng === 'number') {
    return hometown as HometownLocation;
  }
  
  return null;
};

// Helper function to add distance and online status to profiles
const enhanceProfiles = async (profiles: any[], currentUserId: string) => {
  const userLocation = await locationService.getUserLocation(currentUserId);

  return Promise.all(
    profiles.map(async (profile) => {
      let distance = 1; // default fallback
      
      // Only calculate distance if user has location
      if (userLocation) {
        try {
          const calculatedDistance = await locationService.calculateUserDistance(currentUserId, profile.user_id);
          distance = calculatedDistance !== null ? Math.round(calculatedDistance) : 1;
        } catch (error) {
          console.warn(`⚠️ Distance calculation failed for ${profile.user_id}:`, error);
          distance = 1; // fallback
        }
      }

      // Use the helper function to get photos
      const photoUrls = getPhotoUrls(profile);

      // Determine online status - handle missing users data
      const isOnline = profile.users?.is_active && 
        new Date(profile.users?.last_seen || 0) > new Date(Date.now() - 15 * 60 * 1000);

      return {
        ...profile,
        photo_urls: photoUrls, // Normalize to photo_urls
        distance,
        isOnline: isOnline || false, // Default to false if no user data
        last_seen: profile.users?.last_seen || null
      };
    })
  );
};

export const discoveryService = {
  async fetchNearbyUsers(userId: string, filters: FilterState, profileLimit: number, subscriptionTier: string, sortBy: SortingType = "nearby") {
    if (!userId) return [];
    
    console.log('🔍 Starting discovery fetch with filters:', filters, 'sortBy:', sortBy);
    console.log('🕐 Current time:', new Date().toISOString());
    
    // Get current user's location first
    const userLocation = await locationService.getUserLocation(userId);
    console.log('📍 User location:', userLocation ? 'found' : 'not found');
    
    // First clear any expired cache entries to prevent stale data issues
    await this.clearExpiredCache(userId);
    
    // Check cache first (include sortBy in cache key logic)
    const { data: cache, error: cacheError } = await supabase
      .from('visible_users_cache')
      .select('*')
      .eq('user_id', userId)
      .gt('cache_expires_at', new Date().toISOString())
      .single();

    if (cacheError && cacheError.code !== 'PGRST116') {
      console.warn('⚠️ Cache query error:', cacheError);
    }

    let visibleUserIds: string[] = [];
    
    if (cache && cache.visible_user_ids && cache.visible_user_ids.length > 0) {
      // Use cached user IDs
      visibleUserIds = cache.visible_user_ids;
      console.log('✅ Using cached profile list:', visibleUserIds.length, 'profiles');
      console.log('📅 Cache expires at:', cache.cache_expires_at);
    } else {
      console.log('🔄 Cache miss or expired, fetching fresh profiles...');
      
      try {
        // Query new users and cache the results
        let query = supabase
          .from('user_profiles')
          .select('user_id, photo_urls, photos')
          .neq('user_id', userId)
          .eq('is_visible', true)
          .eq('is_profile_complete', true)
          .gte('age', filters.ageMin)
          .lte('age', filters.ageMax);

        if (filters.withPhotoOnly) {
          // Check for photos in either column
          query = query.or('photo_urls.not.is.null,photos.not.is.null');
        }

        const { data: profiles, error } = await query.limit(200);
        
        if (error) {
          console.error('❌ Error fetching profiles:', error);
          throw error;
        }
        
        console.log('📊 Raw profiles from DB:', profiles?.length || 0);
        
        // Filter out users without photos if withPhotoOnly is enabled
        let filteredProfiles = profiles || [];
        if (filters.withPhotoOnly) {
          filteredProfiles = profiles?.filter(p => {
            const photos = getPhotoUrls(p);
            return photos.length > 0;
          }) || [];
          console.log('📸 After photo filter:', filteredProfiles.length);
        }
        
        visibleUserIds = filteredProfiles.map(p => p.user_id);
        console.log('👥 Profile IDs to cache:', visibleUserIds);
        
        // Cache the results with current location and longer expiration
        const cacheExpiration = new Date(Date.now() + 30 * 60 * 1000).toISOString(); // 30 minutes
        const { error: cacheUpsertError } = await supabase
          .from('visible_users_cache')
          .upsert({
            user_id: userId,
            visible_user_ids: visibleUserIds,
            last_location_lat: userLocation?.latitude,
            last_location_lng: userLocation?.longitude,
            cache_expires_at: cacheExpiration,
            updated_at: new Date().toISOString()
          });
        
        if (cacheUpsertError) {
          console.error('❌ Failed to cache results:', cacheUpsertError);
          // Don't throw - continue with uncached results
        } else {
          console.log('💾 Created new profile cache:', visibleUserIds.length, 'profiles, expires:', cacheExpiration);
        }
        
      } catch (error) {
        console.error('❌ Critical error in profile fetching:', error);
        // Fallback: try to get any existing cache even if expired
        const { data: fallbackCache } = await supabase
          .from('visible_users_cache')
          .select('*')
          .eq('user_id', userId)
          .single();
        
        if (fallbackCache && fallbackCache.visible_user_ids) {
          console.log('🚨 Using expired cache as fallback:', fallbackCache.visible_user_ids.length, 'profiles');
          visibleUserIds = fallbackCache.visible_user_ids;
        } else {
          console.log('💥 No fallback cache available, returning empty results');
          return [];
        }
      }
    }

    // Filter by distance ONLY if user has location AND maxDistance is set and reasonable AND sorting by nearby
    if (userLocation && filters.maxDistance > 0 && filters.maxDistance < 1000 && sortBy === "nearby") {
      console.log(`🗺️ Applying distance filter: ${filters.maxDistance}km`);
      const usersWithinDistance: string[] = [];
      
      for (const targetUserId of visibleUserIds) {
        try {
          const distance = await locationService.calculateUserDistance(userId, targetUserId);
          if (distance !== null && distance <= filters.maxDistance) {
            usersWithinDistance.push(targetUserId);
          } else if (distance === null) {
            // Include users where distance calculation fails (missing location data)
            console.log(`⚠️ Distance calculation failed for user ${targetUserId}, including anyway`);
            usersWithinDistance.push(targetUserId);
          }
        } catch (error) {
          console.error(`❌ Error calculating distance for user ${targetUserId}:`, error);
          // Include user if distance calculation fails
          usersWithinDistance.push(targetUserId);
        }
      }
      
      console.log(`📏 After distance filter (${filters.maxDistance}km): ${usersWithinDistance.length} users`);
      visibleUserIds = usersWithinDistance;
    } else if (sortBy === "nearby") {
      console.log('🌍 Skipping distance filter - no location or unrealistic distance');
    }

    // Apply subscription limits
    const limitedUserIds = visibleUserIds.slice(0, profileLimit);
    
    if (visibleUserIds.length > profileLimit) {
      console.log(`🔒 Limiting profiles: ${visibleUserIds.length} -> ${profileLimit} (${subscriptionTier} tier)`);
    }

    // Get full profile data for limited set
    if (limitedUserIds.length === 0) {
      console.log('❌ No user IDs to fetch profiles for');
      return [];
    }

    console.log('🔍 Fetching full profiles for:', limitedUserIds.length, 'users');

    // Use left join to get both profile and user data
    const { data: fullProfiles, error: profileError } = await supabase
      .from('user_profiles')
      .select(`
        *,
        users(id, last_seen, is_active, username)
      `)
      .in('user_id', limitedUserIds);

    if (profileError) {
      console.error('❌ Error fetching full profiles:', profileError);
      throw profileError;
    }

    console.log('✅ Full profiles fetched:', fullProfiles?.length || 0);

    // Calculate real distances and add location data
    const profilesWithDistance = await Promise.all(
      (fullProfiles || []).map(async (profile) => {
        let distance = 1; // default fallback
        
        // Only calculate distance if user has location
        if (userLocation) {
          try {
            const calculatedDistance = await locationService.calculateUserDistance(userId, profile.user_id);
            distance = calculatedDistance !== null ? Math.round(calculatedDistance) : 1;
          } catch (error) {
            console.warn(`⚠️ Distance calculation failed for ${profile.user_id}:`, error);
            distance = 1; // fallback
          }
        }

        // Use the helper function to get photos
        const photoUrls = getPhotoUrls(profile);

        // Determine online status but don't filter by it - handle missing users data
        const isOnline = profile.users?.is_active && 
          new Date(profile.users?.last_seen || 0) > new Date(Date.now() - 15 * 60 * 1000);

        return {
          ...profile,
          photo_urls: photoUrls, // Normalize to photo_urls
          distance,
          isOnline: isOnline || false, // Default to false if no user data
          last_seen: profile.users?.last_seen || null
        };
      })
    );

    // Apply online filter only if specifically requested
    let finalProfiles = profilesWithDistance;
    if (filters.onlineOnly) {
      const onlineCount = finalProfiles.filter(profile => profile.isOnline).length;
      console.log(`👥 Online filter: ${onlineCount}/${finalProfiles.length} users online`);
      finalProfiles = profilesWithDistance.filter(profile => profile.isOnline);
    }

    // Apply sorting based on sortBy parameter
    if (sortBy === "activity") {
      // Sort by last_seen (most recent first)
      finalProfiles.sort((a, b) => {
        const aLastSeen = a.last_seen ? new Date(a.last_seen).getTime() : 0;
        const bLastSeen = b.last_seen ? new Date(b.last_seen).getTime() : 0;
        return bLastSeen - aLastSeen;
      });
      console.log('🕒 Sorted by activity (most recent first)');
    } else if (sortBy === "nearby" && userLocation) {
      // Sort by distance (nearest first)
      finalProfiles.sort((a, b) => a.distance - b.distance);
      console.log('📍 Sorted by distance (nearest first)');
    } else if (sortBy === "new") {
      // Sort by created_at (newest first)
      finalProfiles.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      console.log('🆕 Sorted by newest profiles first');
    } else if (sortBy === "most_buzzz") {
      // Sort by buzzz_count (highest first)
      finalProfiles.sort((a, b) => (b.buzzz_count || 0) - (a.buzzz_count || 0));
      console.log('🔥 Sorted by most buzzzed profiles first');
    } else if (sortBy === "travelers") {
      // Filter and sort travelers (users with different city from hometown city)
      finalProfiles = finalProfiles.filter(profile => {
        const hometown = parseHometownLocation(profile.hometown);
        // Check if user has both city and hometown.city, and they're different
        return profile.city && hometown?.city && profile.city !== hometown.city;
      });
      console.log('✈️ Filtered travelers based on city difference');
    }

    console.log('🎉 Final profiles returned:', finalProfiles.length);
    return finalProfiles;
  },

  async fetchNewUsers(userId: string, limit: number = 6, filters?: FilterState) {
    let query = supabase
      .from('user_profiles')
      .select(`
        *,
        users(id, last_seen, is_active)
      `)
      .neq('user_id', userId)
      .eq('is_visible', true)
      .eq('is_profile_complete', true);

    // Apply age filters if provided
    if (filters?.ageMin && filters.ageMin > 18) {
      query = query.gte('age', filters.ageMin);
    }
    if (filters?.ageMax && filters.ageMax < 99) {
      query = query.lte('age', filters.ageMax);
    }

    // Apply photo filter if requested
    if (filters?.withPhotoOnly) {
      query = query.not('photo_urls', 'is', null);
    }

    query = query.order('created_at', { ascending: false }).limit(limit);

    const { data: profiles, error } = await query;

    if (error) throw error;
    
    // Enhance profiles with distance and online status
    let enhancedProfiles = await enhanceProfiles(profiles || [], userId);

    // Apply post-enhancement filters
    if (filters?.onlineOnly) {
      enhancedProfiles = enhancedProfiles.filter(profile => profile.isOnline);
    }

    if (filters?.maxDistance && filters.maxDistance > 0) {
      enhancedProfiles = enhancedProfiles.filter(profile => 
        profile.distance && profile.distance <= filters.maxDistance
      );
    }

    return enhancedProfiles;
  },

  async fetchMostBuzzzedUsers(userId: string, limit: number = 6, filters?: FilterState) {
    let query = supabase
      .from('user_profiles')
      .select(`
        *,
        users(id, last_seen, is_active)
      `)
      .neq('user_id', userId)
      .eq('is_visible', true)
      .eq('is_profile_complete', true);

    // Apply age filters if provided
    if (filters?.ageMin && filters.ageMin > 18) {
      query = query.gte('age', filters.ageMin);
    }
    if (filters?.ageMax && filters.ageMax < 99) {
      query = query.lte('age', filters.ageMax);
    }

    // Apply photo filter if requested
    if (filters?.withPhotoOnly) {
      query = query.not('photo_urls', 'is', null);
    }

    query = query.order('buzzz_count', { ascending: false }).limit(limit);

    const { data: profiles, error } = await query;

    if (error) throw error;
    
    // Enhance profiles with distance and online status
    let enhancedProfiles = await enhanceProfiles(profiles || [], userId);

    // Apply post-enhancement filters
    if (filters?.onlineOnly) {
      enhancedProfiles = enhancedProfiles.filter(profile => profile.isOnline);
    }

    if (filters?.maxDistance && filters.maxDistance > 0) {
      enhancedProfiles = enhancedProfiles.filter(profile => 
        profile.distance && profile.distance <= filters.maxDistance
      );
    }

    return enhancedProfiles;
  },

  async fetchTravelerUsers(userId: string, limit: number = 6, filters?: FilterState) {
    let query = supabase
      .from('user_profiles')
      .select(`
        *,
        users(id, last_seen, is_active)
      `)
      .neq('user_id', userId)
      .eq('is_visible', true)
      .eq('is_profile_complete', true)
      .not('hometown', 'is', null)
      .not('city', 'is', null);

    // Apply age filters if provided
    if (filters?.ageMin && filters.ageMin > 18) {
      query = query.gte('age', filters.ageMin);
    }
    if (filters?.ageMax && filters.ageMax < 99) {
      query = query.lte('age', filters.ageMax);
    }

    // Apply photo filter if requested
    if (filters?.withPhotoOnly) {
      query = query.not('photo_urls', 'is', null);
    }

    query = query.limit(limit * 3); // Get more to filter properly

    const { data: profiles, error } = await query;

    if (error) throw error;

    // Filter travelers client-side - users with different city from hometown city
    let travelers = (profiles || []).filter(profile => {
      const hometown = parseHometownLocation(profile.hometown);
      // Check if user has both city and hometown.city, and they're different
      return profile.city && hometown?.city && profile.city !== hometown.city;
    }).slice(0, limit);

    // Enhance profiles with distance and online status
    let enhancedProfiles = await enhanceProfiles(travelers, userId);

    // Apply post-enhancement filters
    if (filters?.onlineOnly) {
      enhancedProfiles = enhancedProfiles.filter(profile => profile.isOnline);
    }

    if (filters?.maxDistance && filters.maxDistance > 0) {
      enhancedProfiles = enhancedProfiles.filter(profile => 
        profile.distance && profile.distance <= filters.maxDistance
      );
    }

    return enhancedProfiles;
  },

  async clearCache(userId: string) {
    console.log('🧹 Clearing cache for user:', userId);
    const { error } = await supabase
      .from('visible_users_cache')
      .delete()
      .eq('user_id', userId);
    
    if (error) {
      console.error('❌ Failed to clear cache:', error);
    } else {
      console.log('✅ Cache cleared successfully');
    }
  },

  async clearExpiredCache(userId: string) {
    console.log('🧹 Clearing expired cache entries for user:', userId);
    const { error } = await supabase
      .from('visible_users_cache')
      .delete()
      .eq('user_id', userId)
      .lt('cache_expires_at', new Date().toISOString());
    
    if (error) {
      console.error('❌ Failed to clear expired cache:', error);
    } else {
      console.log('✅ Expired cache entries cleared');
    }
  }
};
